import React, { useCallback, useState } from 'react';
import ImageUploading, { ImageListType } from 'react-images-uploading';
import classNames from 'classnames';

import { PDFImageProps } from '../../../utils/models';
import { fileUpload } from '../../../services/fileUpload';
import { getFileTypeAffix } from '../../../utils/utilities';

import Button from '../button';
import Conditional from '../../common/conditional';
import LoadingBadge from '../loading-badge';

import classes from './style.module.scss';

interface Props {
	initialImage: PDFImageProps;
	onSave: (image: PDFImageProps) => void;
}

const ImageReplacement: React.FC<Props> = ({ initialImage, onSave }) => {
	const [isUploading, setUploading] = useState(false);
	const [images, setImages] = useState<ImageListType>([]);

	const handleImageChange = useCallback((imageList: ImageListType) => {
		setImages(imageList);
	}, []);

	const handleSaveImage = async () => {
		if (navigator.onLine) {
			setUploading(true);
	
			const newFiles = await fileUpload(images.map((image) => image.file as File));
			const image = newFiles[0];
	
			onSave({
				caption: image.caption,
				src: image.src,
				refNo: initialImage.refNo,
				format: image.format
			});
		} else {
			const image = images[0];
	
			onSave({
				caption: image.file!.name,
				src: image.dataURL!,
				refNo: initialImage.refNo,
				format: getFileTypeAffix(image.file!.type)
			});
		}
	};

	return ( 
		<div className="p-3">
			<ImageUploading
				multiple={false}
				value={images}
				onChange={handleImageChange}
				maxNumber={1}
				maxFileSize={2_048_000}
				acceptType={IMAGE_TYPES}
			>
				{({ imageList, dragProps, onImageUpload, isDragging, errors }) => (
					<>
						{errors && (
							<div className={classes.error}>
								{errors.maxNumber && <span>Number of selected images exceed maxNumber</span>}
								{errors.acceptType && <span>Your selected file type is not allowed</span>}
								{errors.maxFileSize && <span>Selected file size exceeds maxFileSize</span>}
								{errors.resolution && <span>Selected file does not match your desired resolution</span>}
							</div>
						)}

						<div {...dragProps} role="button" onClick={onImageUpload} className={classNames([classes.container, 'd-flex justify-content-center align-items-center flex-column'])}>
							<i className="bi bi-cloud-upload-fill"></i>
							<p>{isDragging ? "Drop here" : "Click to upload or drag to drop"}</p>
							<span>{IMAGE_TYPES.join(', ')}</span>
						</div>
							
						<div className="d-flex justify-content-between align-items-center">
							{initialImage && (
								<div className={classes.image}>
									<img src={initialImage.src} alt={initialImage.caption} />
								</div>
							)}

							<Conditional isVisible={imageList.length > 0}>
								<div className={classes.separator}>
									<i className="bi bi-arrow-right"></i>
								</div>
							
								{imageList.map((image, index) => (
									<div className={classes.image} key={index}>
										<img src={image.dataURL} alt={image.file?.name} />
									</div>
								))}
							</Conditional>

						</div>

						<div className="d-flex justify-content-end align-items-center mt-4">
							{isUploading ? (
								<LoadingBadge message='Saving changes' />
							) : (
								<Button 
									label="Save changes" 
									onClick={handleSaveImage} 
									disabled={!!errors || imageList.length === 0} 
								/>
							)}
						</div>
					</>
				)}
			</ImageUploading>
		</div>
	);
};

const IMAGE_TYPES = ['jpg', 'png', 'jpeg'];
 
export default ImageReplacement;